import { reachGoal, reportToSentry } from '@/util';
import { createFetchHttpClient } from '@/ai-text-lib/client';
import { sessionHeaders } from '@/ai-text-lib/eligibility';
import { ensureVisitorId } from '@/ab-lib/visitor-id';
import { PoemClient } from './client/poem-client';
import type { IPoemClientOptions, IStanza, TPoemState } from './client/types';
import {
    ATTENTION_MS,
    CLASS_ATTENTION,
    CLASS_BUSY,
    CLASS_REVEALED,
    COPY,
    GOAL_FAIL,
    GOAL_GENERATE,
    GOAL_READY,
    SELECTOR_COMPOSE,
    SELECTOR_STANZA,
    SELECTOR_STANZAS,
    SELECTOR_STATUS,
    selectorForRole,
} from './const';
import {
    failureText,
    fitsFields,
    hasTextBeyondEmoji,
    matchesFields,
    inputsKey,
    isReportable,
    poemOrigin,
    replaceQuestion,
    TPoemOrigin,
} from './domain';

type TField = HTMLInputElement | HTMLTextAreaElement;

// the API's field name → the form's data-role
const INPUT_ROLES: Record<string, string> = {
    prompt: 'ai_prompt',
    toName: 'to_name',
    fromName: 'from_name',
};

// the status line's look (poem-block styles, [data-tone])
type TTone = 'working' | 'hint' | 'error';

/** Sets a field the way typing would, so the form's own listeners see it. */
function fill(field: TField, text: string): void {
    field.value = text;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * The poem block of one card form (templates/cards/music/poem-block.html.twig):
 * the compose button, the status line, the stanza fields, and the guard that
 * keeps the form from being sent without a poem.
 */
export class PoemBlock {
    private readonly form: HTMLFormElement | null;
    private readonly compose: HTMLButtonElement;
    private readonly status: HTMLElement;
    private readonly stanzasBox: HTMLElement;
    private readonly stanzas: HTMLTextAreaElement[];
    private readonly client: PoemClient;
    // the button's labels come from the template, which renders the first one
    private readonly labels: { compose: string; recompose: string };

    // the last poem written on this page and the inputs it was written from
    private generated: string[] | null = null;
    private generatedFrom: string | null = null;
    // one per page: with storage blocked too, ensureVisitorId() mints a new id on
    // every call, and a second click would start a second poem, not follow the first
    private visitorId: string | null = null;

    /** @param transport a seam for tests: the network and the clock */
    constructor(
        private readonly root: HTMLElement,
        transport: Pick<IPoemClientOptions, 'http' | 'sleep' | 'now'> = {
            http: createFetchHttpClient(sessionHeaders),
        }
    ) {
        this.form = root.closest('form');
        this.compose = root.querySelector<HTMLButtonElement>(SELECTOR_COMPOSE)!;
        this.status = root.querySelector<HTMLElement>(SELECTOR_STATUS)!;
        this.stanzasBox = root.querySelector<HTMLElement>(SELECTOR_STANZAS)!;
        this.stanzas = Array.from(
            root.querySelectorAll<HTMLTextAreaElement>(SELECTOR_STANZA)
        );
        this.labels = {
            compose: this.compose.dataset.labelCompose ?? '',
            recompose: this.compose.dataset.labelRecompose ?? '',
        };
        this.client = new PoemClient({
            ...transport,
            onChange: (state) => this.render(state),
        });
    }

    mount(): void {
        this.compose.addEventListener('click', () => void this.onCompose());

        // a saved stanza over a limit cut in the admin since: `maxlength` does not
        // flag a value it did not see typed, and the save would refuse it blind
        if (this.overLimit()) {
            this.say(COPY.stanzaTooLong, 'error');
        }

        for (const role of ['ai_prompt', 'to_name', 'from_name']) {
            this.field(role)?.addEventListener('input', () =>
                this.onInputsChanged()
            );
        }

        // capture on the document runs before the form's own (jQuery) submit
        // handler, so a form without a poem never reaches the card save
        document.addEventListener('submit', (e) => this.onSubmit(e), true);

        // a shown empty stanza is stopped by the browser before any submit event:
        // `invalid` is the only word of it the page gets
        for (const field of this.stanzas) {
            field.addEventListener('invalid', () => this.onStanzaInvalid());
        }
    }

    /** For the `card-created` goal (cardCreator.js): how the poem came about. */
    origin(): TPoemOrigin | null {
        return poemOrigin(this.stanzaTexts(), this.generated);
    }

    private field(role: string): TField | null {
        return (
            this.form?.querySelector<TField>(selectorForRole(role)) ??
            document.querySelector<TField>(selectorForRole(role))
        );
    }

    private value(role: string): string {
        return this.field(role)?.value.trim() ?? '';
    }

    private currentInputs(): string {
        return inputsKey(
            this.value('ai_prompt'),
            this.value('to_name'),
            this.value('from_name')
        );
    }

    private stanzaTexts(): string[] {
        return this.stanzas.map((s) => s.value);
    }

    private templateId(): number {
        const input = this.form?.querySelector<HTMLInputElement>(
            'input[name="template_id"]'
        );
        return Number(input?.value) || 0;
    }

    private async onCompose(): Promise<void> {
        if (this.client.isBusy()) {
            return;
        }

        if (this.value('ai_prompt') === '') {
            this.say(COPY.needPrompt);
            this.field('ai_prompt')?.focus();
            return;
        }

        const question = replaceQuestion(this.stanzaTexts(), this.generated);
        if (question && !window.confirm(question)) {
            return;
        }

        // the anonymous visitor's key: the `visitor_id` cookie general.js sets on
        // every page, and its copy in the body for a frame where no cookie lives
        this.visitorId ??= ensureVisitorId();

        const regenerate = this.generated !== null || this.hasPoem();
        reachGoal(GOAL_GENERATE, regenerate ? { regenerate: '1' } : undefined);

        const from = this.currentInputs();
        await this.client.generate({
            templateId: this.templateId(),
            prompt: this.value('ai_prompt'),
            toName: this.value('to_name'),
            fromName: this.value('from_name'),
            regenerate,
            visitorId: this.visitorId,
        });

        if (this.client.getState().phase === 'done') {
            this.generatedFrom = from;
        }
    }

    private hasPoem(): boolean {
        return this.stanzaTexts().some((text) => text.trim() !== '');
    }

    private render(state: TPoemState): void {
        const busy = state.phase === 'submitting' || state.phase === 'queued';
        this.compose.disabled = busy;
        this.compose.classList.toggle(CLASS_BUSY, busy);
        this.compose.textContent = busy
            ? COPY.composing
            : this.hasPoem()
              ? this.labels.recompose
              : this.labels.compose;

        switch (state.phase) {
            case 'submitting':
                this.say(COPY.working, 'working');
                break;
            case 'queued':
                this.say(
                    state.queuePosition
                        ? COPY.queued(
                              state.queuePosition,
                              state.estimatedWaitSeconds
                          )
                        : COPY.working,
                    'working'
                );
                break;
            case 'done':
                this.showPoem(state.stanzas);
                break;
            case 'error':
                this.showFailure(state);
                break;
        }
    }

    private showPoem(stanzas: IStanza[]): void {
        const byId = new Map(stanzas.map((s) => [s.id, s.text]));
        // a poem of another form (the server hands a running job out only for the
        // same input, but the page may have changed since): empty fields and a
        // "ready" would be a lie
        if (!matchesFields(this.stanzas, byId)) {
            this.showFailure({ phase: 'error', reason: 'mismatch' });
            return;
        }
        // a stanza is never cut to fit (a cut one breaks the rhyme): one the field
        // does not take means the server and the form count the limit apart
        if (!fitsFields(this.stanzas, byId)) {
            this.showFailure({ phase: 'error', reason: 'failed' });
            reportToSentry({
                section: 'ai_poem',
                level: 'error',
                error: new Error('ai poem: a stanza is longer than its field'),
                tags: { ai_poem_failure: 'stanza_overflow' },
            });
            return;
        }

        // shown before filled: the form sizes a textarea to its content on
        // `input`, and a hidden one measures 0
        this.reveal();
        for (const field of this.stanzas) {
            fill(field, byId.get(Number(field.dataset.id)) ?? '');
        }
        this.generated = this.stanzaTexts();
        this.compose.textContent = this.labels.recompose;
        this.say('');
        reachGoal(GOAL_READY);
    }

    private showFailure(state: Extract<TPoemState, { phase: 'error' }>): void {
        this.say(`${failureText(state)} ${COPY.writeYourself}`, 'error');
        // the refused input is the person's to fix: take them to it
        if (state.reason === 'input' && state.field) {
            this.field(INPUT_ROLES[state.field] ?? '')?.focus();
        }
        this.reveal();
        reachGoal(GOAL_FAIL, { reason: state.reason });

        if (isReportable(state)) {
            reportToSentry({
                section: 'ai_poem',
                level: 'warning',
                error: new Error(`ai poem failed: ${state.reason}`),
                // no visitor id, no text: only what explains the failure
                tags: { ai_poem_failure: state.reason },
                extras: { field: state.field ?? '' },
            });
        }
    }

    private onInputsChanged(): void {
        if (
            this.generatedFrom !== null &&
            !this.client.isBusy() &&
            this.currentInputs() !== this.generatedFrom
        ) {
            this.say(COPY.inputsChanged);
        }
    }

    private onStanzaInvalid(): void {
        this.say(
            this.hasPoem() ? COPY.fillAllStanzas : COPY.writeOrCompose,
            'error'
        );
    }

    private onSubmit(e: Event): void {
        if (e.target !== this.form) {
            return;
        }

        // a stanza emptied by hand is left to the form's own required check
        let message: string;
        const optional = this.root.dataset.optional === '1';
        const tooLong = this.overLimit();
        const noWords = this.stanzas.find(
            (field) => field.value.trim() !== '' && !hasTextBeyondEmoji(field.value)
        );
        if (noWords && !this.client.isBusy()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.say(COPY.stanzaNoWords, 'error');
            noWords.focus();
            return;
        }
        if (tooLong && !this.client.isBusy()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.say(COPY.stanzaTooLong, 'error');
            tooLong.focus();
            return;
        }
        if (this.client.isBusy()) {
            message = COPY.stillComposing;
        } else if (this.hasPoem()) {
            // a stanza written makes it a poem, and then all of them (the server
            // refuses a partial one); without `required` the browser does not say
            if (!optional || this.stanzaTexts().every((t) => t.trim() !== '')) {
                return;
            }
            message = COPY.fillAllStanzas;
        } else if (optional) {
            // a card saved without a poem stays without one on an edit
            return;
        } else {
            // fields open after a failure: writing it by hand is the way out too
            message = this.stanzasBox.hidden ? COPY.needPoem : COPY.writeOrCompose;
        }

        e.preventDefault();
        e.stopImmediatePropagation();
        this.say(message, 'error');
        this.attract();
    }

    private overLimit(): Maybe<HTMLTextAreaElement> {
        return this.stanzas.find(
            (field) => field.maxLength > 0 && field.value.length > field.maxLength
        );
    }

    /** Draws the eye to the button the person has to press. */
    private attract(): void {
        this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.compose.classList.add(CLASS_ATTENTION);
        window.setTimeout(
            () => this.compose.classList.remove(CLASS_ATTENTION),
            ATTENTION_MS
        );
    }

    private reveal(): void {
        if (!this.stanzasBox.hidden) {
            return;
        }

        this.stanzasBox.hidden = false;
        this.stanzasBox.classList.add(CLASS_REVEALED);
        const optional = this.root.dataset.optional === '1';
        for (const field of this.stanzas) {
            // required only once shown (see poem-block.html.twig), and never for a
            // card that may stay without a poem: the submit guard checks that one
            field.required = !optional;
            // the form's autoHeight sized these while hidden, to 0: let it measure
            // again, and the length counters with it
            field.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }

    private say(text: string, tone: TTone = 'hint'): void {
        this.status.textContent = text;
        this.status.hidden = text === '';
        this.status.dataset.tone = tone;
    }
}
